import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { approveDenyBillingAdjustment } from "@/lib/api/billing"
import { toast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
} from "@/components/ui/credenza"
import { Input } from "@/components/ui/input"
import { Loader } from "@/components/loader"

interface BillingAdjustmentActionsProps {
  adjustmentData: any // Replace 'any' with a specific type if available
}

export function BillingAdjustmentActions({
  adjustmentData,
}: BillingAdjustmentActionsProps) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [actionType, setActionType] = useState<"Approved" | "Denied" | null>(
    null,
  )
  const [inputValue, setInputValue] = useState("")
  const mutation = useMutation({
    mutationFn: async () => {
      return await approveDenyBillingAdjustment(
        adjustmentData.adjustment_id,
        actionType!.toUpperCase(),
      )
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["billingAdjustment"] })
      toast({
        title: `Adjustment ${actionType!.toUpperCase()}!`,
        description: `Adjustment ${actionType!.toUpperCase()} Successfully!`,
      })
      setOpen(false)
      setInputValue("")
    },
    onError: (error) => {
      console.error(`Error ${actionType}ing adjustment:`, error)
      toast({
        title: "Error",
        description: "Failed to process adjustment.",
        variant: "destructive",
      })
    },
  })
  const handleConfirm = async () => {
    if (!actionType) return
    mutation.mutate()
  }

  return (
    <>
      <div className="flex gap-2">
        <Button
          variant="default"
          size="sm"
          onClick={() => {
            setActionType("Approved")
            setOpen(true)
          }}
        >
          Approve
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => {
            setActionType("Denied")
            setOpen(true)
          }}
        >
          Deny
        </Button>
      </div>

      {/* Confirmation Modal */}
      <Credenza open={open} onOpenChange={setOpen}>
        <CredenzaContent>
          <CredenzaHeader>
            <CredenzaTitle>
              Confirm {actionType === "Approved" ? "Approval" : "Denial"}
            </CredenzaTitle>
          </CredenzaHeader>
          <p className="text-gray-600 dark:text-white">
            Please enter the <strong>Adjustment ID</strong> (
            {adjustmentData.adjustment_id}) to proceed.
          </p>
          <Input
            className="dark:text-white"
            placeholder="Enter Billing ID or Adjustment ID"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <CredenzaFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant={actionType === "Approved" ? "default" : "destructive"}
              disabled={
                Number(inputValue) !== adjustmentData.adjustment_id ||
                mutation.isPending
              } // Disable if loading
              onClick={handleConfirm}
            >
              {mutation.isPending ? (
                <>
                  {Loader()}
                  {actionType === "Approved"
                    ? "Confirm Approve"
                    : "Confirm Deny"}
                </>
              ) : actionType === "Approved" ? (
                "Confirm Approve"
              ) : (
                "Confirm Deny"
              )}
            </Button>

            {/* <Button
              variant={actionType === "Approved" ? "default" : "destructive"}
              disabled={Number(inputValue) !== adjustmentData.adjustment_id} // Ensure proper type comparison
              onClick={handleConfirm}
            >
              {actionType === "Approved" ? "Confirm Approve" : "Confirm Deny"}
            </Button> */}
          </CredenzaFooter>
        </CredenzaContent>
      </Credenza>
    </>
  )
}
